import React, { useEffect, useMemo, useState } from 'react';

import { check } from '@/assets/icons';
import convertSetlitToOpenSetlistConfig from '@/components/Layout/components/GenerateSetlist/helpers/convertSetlitToOpenSetlistConfig';
import { RoundedButton } from '@/components/shared/RoundedButton';
import { DAYS_OF_WEEK } from '@/config/constants';
import { ListMyTeamSetlistsQuery, useListMySetlistFeedbackRequestsQuery } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { useServices } from '@/providers/ServicesProvider';
import { format, startOfWeek } from 'date-fns';

import dashboardStyles from '../../Dashboard.module.scss';
import styles from './WeeklyPanel.module.scss';

export type WeeklyPanelProps = {
	onInviteClick: () => void;
	currentSetlist: ListMyTeamSetlistsQuery['listMyTeamSetlists'][number];
};

export const WeeklyPanel: React.FC<WeeklyPanelProps> = ({ onInviteClick, currentSetlist }) => {
	const { services } = useServices();
	const { user } = useAuth();
	const { openQuickAddSongs, openSetlistWithCallback } = useLayout();
	const { data: feedbackData } = useListMySetlistFeedbackRequestsQuery();

	const fireAddSongs = useAnalyticsEvent('WeeklyPanel_CTA_Add_Songs');
	const fireEditSetlist = useAnalyticsEvent('WeeklyPanel_CTA_Edit_Setlist');
	const fireAddPeople = useAnalyticsEvent('WeeklyPanel_CTA_Add_People');
	const fireGiveFeedback = useAnalyticsEvent('WeeklyPanel_CTA_Give_Feedback');

	const serviceDayName = useMemo(() => {
		if (!services[0]) return null;
		return DAYS_OF_WEEK[services[0].day > 6 ? 0 : services[0].day];
	}, [services]);

	// Both keys reset each Monday — steps must be re-earned each week
	const weekOf = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
	const STEP1_KEY = `weeklyPanel_songsAdded_${weekOf}`;
	const STEP2_KEY = `weeklyPanel_setlistEdited_${weekOf}`;

	const [step1Done, setStep1Done] = useState(
		() => localStorage.getItem(STEP1_KEY) === 'true',
	);
	const [step2Done, setStep2Done] = useState(
		() => localStorage.getItem(STEP2_KEY) === 'true',
	);

	// The useState initializers above only run once, at mount — if this
	// component stays mounted across a Monday-midnight-UTC-adjacent week
	// rollover, STEP1_KEY/STEP2_KEY change but step1Done/step2Done would
	// otherwise keep reading last week's completed-state. Re-derive from
	// localStorage whenever the week key changes so steps are re-earned.
	useEffect(() => {
		setStep1Done(localStorage.getItem(STEP1_KEY) === 'true');
		setStep2Done(localStorage.getItem(STEP2_KEY) === 'true');
	}, [STEP1_KEY, STEP2_KEY]);

	const setlistDayName = currentSetlist
		? format(new Date(currentSetlist.startAt), 'EEEE')
		: (serviceDayName ?? 'Sunday');

	const heading = `${setlistDayName} is coming`;

	const step1Complete = step1Done;
	const step2Complete = step1Complete && step2Done;

	const step3Complete = useMemo(() => {
		if (!step2Complete || !currentSetlist) return false;
		const pastServiceTime = new Date() > new Date(currentSetlist.startAt);
		const hasReceivedFeedbackRequest = !!feedbackData?.listMySetlistFeedbackRequests.find(
			(f) => f.id === currentSetlist.id,
		);
		return pastServiceTime || hasReceivedFeedbackRequest;
	}, [step2Complete, currentSetlist, feedbackData]);

	const step4Complete = useMemo(() => {
		if (!step3Complete || !currentSetlist) return false;
		const hasFeedback = !!currentSetlist.feedback;
		const hasUserSubmission = !!currentSetlist.submissions?.some(
			(sub) => sub.userId === user?.id,
		);
		return hasFeedback || hasUserSubmission;
	}, [step3Complete, currentSetlist, user?.id]);

	const step1Active = !step1Complete;
	const step2Active = step1Complete && !step2Complete;
	const step3Active = step2Complete && !step3Complete;
	const step4Active = step3Complete && !step4Complete;

	const serviceCountdown = useMemo(() => {
		const diffMs = new Date(currentSetlist.startAt).getTime() - Date.now();
		if (diffMs <= 0) return null;
		const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
		if (diffHours < 24) return diffHours <= 1 ? 'in about an hour' : `in ${diffHours} hours`;
		const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
		return diffDays === 1 ? 'tomorrow' : `in ${diffDays} days`;
	}, [currentSetlist.startAt]);

	const handleAddSongs = () => {
		fireAddSongs();
		localStorage.setItem(STEP1_KEY, 'true');
		setStep1Done(true);
		openQuickAddSongs();
	};

	const handleEditSetlist = () => {
		fireEditSetlist();
		localStorage.setItem(STEP2_KEY, 'true');
		setStep2Done(true);
		openSetlistWithCallback(() => {}, convertSetlitToOpenSetlistConfig(currentSetlist));
	};

	const handleAddPeople = () => {
		fireAddPeople();
		onInviteClick();
	};

	const handleGiveFeedback = () => {
		if (!currentSetlist) return;
		fireGiveFeedback();
		openSetlistWithCallback(() => {}, convertSetlitToOpenSetlistConfig(currentSetlist));
	};

	return (
		<div className={styles['root']}>
			<div className={dashboardStyles['title']}>{heading}</div>
			<div className={styles['items']}>
				<WeeklyStepItem
					number={1}
					title="Add more songs"
					subtitle="Import yours or browse"
					isCompleted={step1Complete}
					isActive={step1Active}
					onAction={handleAddSongs}
					buttonComponent={
						<RoundedButton color={step1Complete ? 'default' : 'blue'} onClick={handleAddSongs}>
							Add songs
						</RoundedButton>
					}
				/>
				<WeeklyStepItem
					number={2}
					title="Edit your setlist"
					subtitle="Regenerate or craft yourself"
					isCompleted={step2Complete}
					isActive={step2Active}
					onAction={handleEditSetlist}
					buttonComponent={
						<RoundedButton color={step2Complete ? 'default' : 'blue'} onClick={handleEditSetlist}>
							Edit setlist
						</RoundedButton>
					}
				/>
				<WeeklyStepItem
					number={3}
					title={`Use it on ${setlistDayName}`}
					subtitle="Share your setlist with the team"
					isCompleted={step3Complete}
					isActive={step3Active}
					onAction={handleAddPeople}
					buttonComponent={
						<RoundedButton color={step3Complete ? 'default' : 'blue'} onClick={handleAddPeople}>
							Add people
						</RoundedButton>
					}
				/>
				<WeeklyStepItem
					number={4}
					isLast
					title={step3Complete ? 'Give feedback' : serviceCountdown ? `Give feedback ${serviceCountdown}` : 'Give feedback'}
					subtitle={step3Complete ? 'Reflect and improve weekly' : `Available after ${setlistDayName}'s service`}
					isCompleted={step4Complete}
					isActive={step4Active}
					onAction={handleGiveFeedback}
					buttonComponent={
						<RoundedButton color={step4Complete ? 'default' : 'blue'} onClick={handleGiveFeedback}>
							Give feedback
						</RoundedButton>
					}
				/>
			</div>
		</div>
	);
};

const WeeklyStepItem: React.FC<{
	number: number;
	isLast?: boolean;
	title: string;
	subtitle: string;
	isCompleted?: boolean;
	isActive?: boolean;
	onAction?: () => void;
	buttonComponent: React.ReactNode;
}> = ({ number, title, subtitle, isCompleted, isActive, onAction, buttonComponent, isLast }) => {
	return (
		<div
			className={classNames([
				styles['item'],
				isCompleted ? styles['completed'] : '',
				isActive ? styles['active'] : '',
				isLast ? styles['last'] : '',
			])}
			onClick={(isActive || isCompleted) ? onAction : undefined}
		>
			<div className={styles['timeline']}>
				<div className={styles['number']}>
					{isCompleted ? check({}) : number}
				</div>
				{!isLast ? <div className={styles['line']} /> : null}
			</div>
			<div className={styles['content']}>
				<div className={styles['title']}>{title}</div>
				<div className={styles['subtitle']}>{subtitle}</div>
			</div>
			<div className={styles['actions']}>
				{(isActive || isCompleted) ? buttonComponent : null}
			</div>
		</div>
	);
};
